import {Component,OnDestroy,OnInit} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {Session} from "@domain/security/session";
import {ActivatedRoute,NavigationEnd,Router} from "@angular/router";
import {isRouteAllowed} from "@core/security/role-admin-helpers";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs"
import {Subscription} from "rxjs";
import {filter} from "rxjs/operators";

/**
 * Enum pour les noms des différents onglets de la page ndf
 */
export enum Onglets {
    NDF = "Ndf",
    FRAIS_CARTES = "FraisCartes",
    SUIVI_RELEVES = "SuiviReleves"
}

/**
 * Sous-menu ndf
 *
 * @author Guillaume TRAVOT
 * @date 21/11/2023
 */
@Component({
    host: {'data-test-id': 'frais-ndf'},
    templateUrl: './frais-ndf.component.html'
})
export class FraisNdfComponent implements OnInit, OnDestroy, IComponentWithRoutedTabs {
    /** Liste statique des onglets */
    public static listeOnglets: Array<PageHeaderItem> = [
        {
            code: Onglets.NDF,
            libelle: 'admin.frais.ndf.onglets.ndf',
            url: 'Admin/Frais/Ndf/Ndf'
        }, {
            code: Onglets.FRAIS_CARTES,
            libelle: 'admin.frais.ndf.onglets.fraisCartes.title',
            url: 'Admin/Frais/Ndf/FraisCartes'
        }, {
            code: Onglets.SUIVI_RELEVES,
            libelle: 'admin.frais.ndf.onglets.cartes',
            url: 'Admin/Frais/Ndf/SuiviReleves'
        }
    ];

    /** Liste des différents onglets disponibles dans le menu frais */
    listeTabItems: Array<PageHeaderItem> = FraisNdfComponent.listeOnglets;

    /** Onglet courant */
    selectedItem: PageHeaderItem;

    /** Référence vers l'enum des onglets entreprise pour l'utiliser dans le template */
    Onglet = Onglets;

    /** Index de l'onglet courant */
    selectedIndex: number;

    /** Indique si le header doit être affiché */
    isDisplayed: boolean;

    /** Session */
    private session: Session;

    /** Souscription */
    private subscription: Subscription;

    /** Indique si on est sur la page de liste des erreurs */
    isOnErrorList: boolean = false;

    constructor(
        protected translateService: TranslateService,
        private router: Router,
        private store: Store<AppState>,
        private activatedRoute: ActivatedRoute,
    ) { }

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Récupération de la session
        this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe((session: Session) => {
            //Stockage de la session
            this.session = session;

            //Filtrage des onglets autorisés
            this.listeTabItems = this.listeTabItems.filter(i => isRouteAllowed(this.router, this.session, i.url));

            //Mise à jour initiale de l'état de la route
            this.updateRouteState();

            //Écoute des changements de navigation
            this.subscription = this.router.events.pipe(
                filter(event => event instanceof NavigationEnd)
            ).subscribe(() => {
                this.updateRouteState();
            });
        });
    }

    /**
     * Met à jour l'état en fonction de la route courante
     */
    private updateRouteState(): void {
        //Récupération de la route
        const firstChild = this.activatedRoute.snapshot.firstChild;

        //Si pas de route enfant, on sort
        if (!firstChild) return;

        //Extraction du path
        const path = firstChild.url[0]?.path;

        //On vérifie si on trouve l'onglet demandé via l'url
        const matching = this.listeTabItems.find(i => i.code === path);

        //Détection de la page des erreurs (route avec idLot)
        this.isOnErrorList = !!firstChild.params['idLot'];

        //Si on trouve bien l'onglet correspondant
        if (!!matching) {
            this.selectedItem = matching;
            this.selectedIndex = this.listeTabItems.findIndex(i => i.code === path);
            this.isDisplayed = true;
        } else {
            this.selectedItem = this.listeTabItems[0];
            this.selectedIndex = 0;
            this.isDisplayed = true;
        }
    }

    /**
     * Changement d'onglet
     *
     * @param selectedItem Onglet sélectionné
     */
    onSelectedItemChange(selectedItem: PageHeaderItem): void {
        if (!this.selectedItem) {
            this.selectedItem = selectedItem;
        }
        if (selectedItem?.code !== this.selectedItem?.code) {
            this.selectedItem = selectedItem;
            this.router.navigateByUrl(selectedItem.url);
        }
    }

    /**
     * Retour à la liste des relevés
     */
    goBack(): void {
        this.router.navigate(['Admin/Frais/Ndf/SuiviReleves']);
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy() {
        //Résiliation de l'abonnement
        this.subscription.unsubscribe();
    }
}
